import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ClickableAvatar } from "./ClickableAvatar";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import api from "../lib/api";
import { Target, Plus, X, Scale, Ruler } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GOAL_TYPES = [
  { value: "Weight Loss", label: "Weight Loss" },
  { value: "Weight Gain", label: "Weight Gain" },
  { value: "Muscle Building", label: "Muscle Building" },
  { value: "General Fitness", label: "General Fitness" },
  { value: "Custom", label: "Custom" },
];

const MEASUREMENTS = [
  { id: "weight", label: "Weight", unit: "kg", icon: Scale },
  { id: "chest", label: "Chest", unit: "cm", icon: Ruler },
  { id: "waist", label: "Waist", unit: "cm", icon: Ruler },
  { id: "arm", label: "Arm", unit: "cm", icon: Ruler },
];

export function SetGoalModal({
  open,
  onOpenChange,
  member,
  branchId,
  existingGoal,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    goalType: "Weight Loss",
    reminderDays: 7,
    allowMemberToRecord: false,
  });
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);

  useEffect(() => {
    if (existingGoal) {
      setForm({
        name: existingGoal.name || "",
        description: existingGoal.description || "",
        goalType: existingGoal.goalType || "Weight Loss",
        reminderDays: existingGoal.reminderDays || 7,
        allowMemberToRecord: existingGoal.allowMemberToRecord || false,
      });
      setSelectedMeasurements(
        existingGoal.measurements?.map((m) => ({
          metric: m.metric,
          currentValue: m.currentValue,
          targetValue: m.targetValue,
        })) || [],
      );
    } else {
      setForm({
        name: "",
        description: "",
        goalType: "Weight Loss",
        reminderDays: 7,
        allowMemberToRecord: false,
      });
      setSelectedMeasurements([]);
    }
  }, [existingGoal, open]);

  const toggleMeasurement = (metricId) => {
    const exists = selectedMeasurements.find((m) => m.metric === metricId);
    if (exists) {
      setSelectedMeasurements(
        selectedMeasurements.filter((m) => m.metric !== metricId),
      );
    } else {
      setSelectedMeasurements([
        ...selectedMeasurements,
        {
          metric: metricId,
          currentValue: metricId === "weight" ? member?.weight || 70 : 0,
          targetValue: 0,
        },
      ]);
    }
  };

  const updateMeasurement = (metric, field, value) => {
    setSelectedMeasurements(
      selectedMeasurements.map((m) =>
        m.metric === metric ? { ...m, [field]: parseFloat(value) || 0 } : m,
      ),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Goal name is required");
      return;
    }
    if (selectedMeasurements.length === 0) {
      toast.error("Select at least one measurement to track");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        memberId: member.id,
        branchId: branchId || member.branchId || "",
        name: form.name,
        description: form.description,
        goalType: form.goalType,
        reminderDays: form.reminderDays,
        measurements: selectedMeasurements,
        allowMemberToRecord: form.allowMemberToRecord,
      };

      if (existingGoal) {
        await api.put(`/goals/${existingGoal.id}`, payload);
        toast.success("Goal updated successfully");
      } else {
        await api.post("/goals", payload);
        toast.success("Goal created successfully");
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save goal");
    } finally {
      setLoading(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-2 sm:p-4 gap-2 "
        data-testid="set-goal-modal"
      >
        <DialogHeader className="pb-3 pt-2 border-b border-border shrink-0 space-y-0">
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            {existingGoal ? "Edit Goal" : "Set New Goal"}
          </DialogTitle>
          <DialogDescription className="hidden md:block">
            Create a personalized fitness goal with measurable targets
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto  md:space-y-4 py-2 sm:py-4"
        >
          {/* Member Info */}
          <div className="flex items-center gap-3 p-0 bg-secondary/30 rounded-lg">
            <ClickableAvatar
              src={member.photo}
              name={member.name}
              className="h-12 w-12 border border-border"
              fallbackClassName="bg-primary/10 text-primary font-heading"
            />
            <div>
              <p className="font-medium">{member.name}</p>
              <p className="text-xs text-muted-foreground font-mono">
                {member.phone}
              </p>
            </div>
          </div>

          {/* Goal Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 px-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Goal Name *
              </Label>
              <Input
                placeholder="e.g., Lose 10kg in 3 months"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="goal-name-input"
              />
            </div>
            <div className="space-y-2 px-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Goal Type
              </Label>
              <Select
                value={form.goalType}
                onValueChange={(v) => setForm({ ...form, goalType: v })}
              >
                <SelectTrigger data-testid="goal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 px-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Description (Optional)
            </Label>
            <Textarea
              placeholder="Add details about this goal..."
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="min-h-[60px]"
            />
          </div>

          {/* Measurement Targets */}
          <div className="space-y-3 px-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Select Measurements to Track
            </Label>
            <div className="flex flex-wrap gap-2">
              {MEASUREMENTS.map((m) => {
                const isSelected = selectedMeasurements.some(
                  (sm) => sm.metric === m.id,
                );
                return (
                  <Badge
                    key={m.id}
                    variant={isSelected ? "default" : "outline"}
                    className={`cursor-pointer px-3 py-1.5 ${isSelected ? "bg-primary" : ""}`}
                    onClick={() => toggleMeasurement(m.id)}
                  >
                    <m.icon className="h-3 w-3 mr-1" />
                    {m.label}
                    {isSelected && <X className="h-3 w-3 ml-1" />}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Measurement Values */}
          {selectedMeasurements.length > 0 && (
            <div className="space-y-3 px-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Set Target Values
              </Label>
              <div className="space-y-3">
                {selectedMeasurements.map((sm) => {
                  const measurementInfo = MEASUREMENTS.find(
                    (m) => m.id === sm.metric,
                  );
                  return (
                    <div
                      key={sm.metric}
                      className="grid grid-cols-3 gap-3 p-3 bg-secondary/20 rounded-lg"
                    >
                      <div className="col-span-3 sm:col-span-1">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <measurementInfo.icon className="h-4 w-4 text-primary" />
                          {measurementInfo.label}
                        </p>
                        {measurementInfo.unit && (
                          <p className="text-xs text-muted-foreground">
                            Unit: {measurementInfo.unit}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">
                          Current
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={sm.currentValue}
                          onChange={(e) =>
                            updateMeasurement(
                              sm.metric,
                              "currentValue",
                              e.target.value,
                            )
                          }
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">
                          Target
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={sm.targetValue}
                          onChange={(e) =>
                            updateMeasurement(
                              sm.metric,
                              "targetValue",
                              e.target.value,
                            )
                          }
                          className="h-9"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reminder Frequency */}
          <div className="space-y-3 px-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Progress Reminder
              </Label>
              <Badge variant="outline" className="font-mono">
                Every {form.reminderDays} days
              </Badge>
            </div>
            <Slider
              value={[form.reminderDays]}
              onValueChange={(v) => setForm({ ...form, reminderDays: v[0] })}
              min={1}
              max={31}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              You'll be reminded to update progress every {form.reminderDays}{" "}
              day{form.reminderDays > 1 ? "s" : ""}
            </p>
          </div>

          {/* Allow Member to Record Progress */}
          <div className="flex items-center space-x-2 px-2 py-3 border border-border/50 rounded-lg bg-secondary/10">
            <Checkbox
              id="allowMemberToRecord"
              checked={form.allowMemberToRecord}
              onCheckedChange={(checked) =>
                setForm({ ...form, allowMemberToRecord: !!checked })
              }
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="allowMemberToRecord"
                className="text-xs font-semibold uppercase font-mono tracking-wide cursor-pointer select-none"
              >
                Can member add progress?
              </label>
              <p className="text-[11px] text-muted-foreground">
                If checked, the member will be able to log their own progress
                values in the member portal.
              </p>
            </div>
          </div>
        </form>

        <div className="pt-4 border-t border-border shrink-0">
          <Button
            type="submit"
            className="w-full h-11 font-heading uppercase tracking-wider"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="save-goal-btn"
          >
            {loading
              ? "Saving..."
              : existingGoal
                ? "Update Goal"
                : "Create Goal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
